import { useEffect, useRef, useState } from "react";
import useWebPost from "../../utils/useWebPost";
import ImgUploader from "../../components/ImgUploader";
import { Button, Table, Form, Input, message, Space, Popconfirm,Pagination } from "antd";
import renderFileList from "../../components/renderFileList";
import { checkp } from "../../utils/functions";
import CustModal from "../../components/ShowModal";
import { PlusOutlined } from "@ant-design/icons";
import FullHeightLayout from "../../components/FullHeightLayout";
import DateInput from "../../components/DateInput";

const { TextArea } = Input;

function WeldingList() {
    const webPost = useWebPost();
    const [kw, setKw] = useState('');
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(1);
    const [loading, setLoading] = useState(true);
    const [currentItem, setCurrentItem] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [form] = Form.useForm();
    const loadingRef = useRef(false);
    const pageSizeOptions = [10, 20, 50, 100, 500];

    const columns = [
        { title: '报告编号', dataIndex: 'report_no', width: 120 },
        { title: '报告日期', dataIndex: 'report_date', width: 120 },
        { title: '焊接方法', dataIndex: 'weld_method', width: 120 },
        { title: '母材1材质', dataIndex: 'base1_material', width: 120 },
        { title: '母材1规格(mm)', dataIndex: 'base1_spec', width: 140 },
        { title: '母材2材质', dataIndex: 'base2_material', width: 120 },
        { title: '母材2规格(mm)', dataIndex: 'base2_spec', width: 140 },
        { title: '母材厚度范围(mm)', dataIndex: 'thickness_range', width: 160 },
        {
            title: '焊接工艺评定报告',
            dataIndex: 'process_report',
            render: (file) => renderFileList(file)
        },
        { title: '说明', dataIndex: 'remark' },
        checkp('tszm') && {
            title: '操作',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Button onClick={() => handleEdit(record)}>
                        修改
                    </Button>
                    <Popconfirm
                        title="确定删除该条数据吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" danger>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ].filter(Boolean);

    // 获取列表数据
    useEffect(() => {
        fetchList();
    }, []);

    const fetchList = async (_page,_pagesize) => {
        setLoading(true);
        try {
            const params={page: _page || page, limit:_pagesize || pageSize, kw};
            const res = await webPost('getWeldingProcess',params);
            if (res && res.data) {
                setData(res.data);
                setTotal(res.total);
            }
        } catch (err) {
            message.error('数据加载失败');
        } finally {
            setLoading(false);
        }
    };

    // 新增
    const handleAdd = () => {
        setCurrentItem(null);
        setModalVisible(true);
        form.resetFields();
    };

    // 修改
    const handleEdit = (record) => {
        setCurrentItem(record);
        setModalVisible(true);
        form.setFieldsValue(record);
    };

    // 删除
    const handleDelete = async (id) => {
        try {
            const res = await webPost('deleteWeldingProcess', { id });
            if (res) {
                message.success('删除成功');
                fetchList();
            }
        } catch (err) {
            message.error('删除失败');
        }
    };

    // 提交保存
    const handleSubmit = async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;

        try {
            const values = await form.validateFields();
            const params = { ...values };

            if (currentItem) {
                params.id = currentItem.id; // 修改带ID
            }

            const res = await webPost('saveWeldingProcess', params);
            if (res) {
                message.success(currentItem ? '修改成功' : '新增成功');
                setModalVisible(false);
                fetchList();
            }
        } catch (err) {
            message.error('保存失败：' + err.message);
        } finally {
            loadingRef.current = false;
        }
    };
    const onPageChange = (page, size) => {
        setPage(page);
        if (size !== pageSize) setPageSize(size);
        fetchList(page,size);
    };
    return (
        <FullHeightLayout
            headerContent={<Space>
                <Input onChange={(e) => setKw(e.target.value)} />
                <Button type="primary" onClick={fetchList} >搜索</Button>
                <Button
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    style={{ position: "absolute", zIndex: 10, right: 16, top: 8 }}
                >
                    新增焊接工艺评定
                </Button>
            </Space>}
            bodyContent={null}
            footerContent={<div style={{
                display: 'flex', justifyContent: 'flex-end',
                alignItems: 'center',
            }}>
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(_total, range) => `第 ${range[0]}-${range[1]} 条，共 ${_total} 条`}
                    onChange={onPageChange}
                    pageSizeOptions={pageSizeOptions}
                />
            </div>}
        >
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{ x: 1600 }}
            />

            <CustModal
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
            >
                <Form form={form} layout="vertical" labelAlign="left">
                    <Form.Item
                        label="报告编号"
                        name="report_no"
                        rules={[{ required: true, message: '请输入报告编号' }]}
                    >
                        <Input placeholder="请输入" disabled={!!currentItem} />
                    </Form.Item>

                    <Form.Item
                        label="报告日期"
                        name="report_date"
                        rules={[{ required: true }]}
                    >
                        <DateInput />
                    </Form.Item>

                    <Form.Item label="焊接方法" name="weld_method" rules={[{ required: true }]}>
                        <Input placeholder="如：氩弧焊、电弧焊等" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item label="母材1材质" name="base1_material" rules={[{ required: true }]} style={{ flex: 1 }}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="母材1规格(mm)" name="base1_spec" rules={[{ required: true }]} style={{ flex: 1 }}>
                            <Input />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item label="母材2材质" name="base2_material" rules={[{ required: true }]} style={{ flex: 1 }}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="母材2规格(mm)" name="base2_spec" rules={[{ required: true }]} style={{ flex: 1 }}>
                            <Input />
                        </Form.Item>
                    </div>

                    <Form.Item label="母材厚度范围(mm)" name="thickness_range" rules={[{ required: true }]}>
                        <Input placeholder="如：2-10mm" />
                    </Form.Item>

                    <Form.Item label="焊接工艺评定报告" name="process_report">
                        <ImgUploader />
                    </Form.Item>

                    <Form.Item label="说明" name="remark">
                        <TextArea rows={3} placeholder="补充说明" />
                    </Form.Item>
                </Form>
            </CustModal>
        </FullHeightLayout>
    );
}

export default WeldingList;